/**
  * @class meleActorZoom
  * @description
  *
  * # Purpose
  * Implements `<mele-actor-zoom>` custom web element for showing camera zoom in <mele-channel>
  */

import meleActor from 'meleActor'
const Algo = require('../../msys/msysAV/src_em/algo.js')
const Draw = require('../../msys/msysAV/src_em/draw.js')

const PROPERTIES = _.extend({}, {
  zoom: { description: 'current camera zoom level', type: 'number' },
  levels: { description: 'list of zoom levels', type: 'array', items: { type: 'number' }, default: [] },
  zooming: { description: 'indicates zooming is in progress', type: 'boolean' },
  settings: {
    description: 'whether to show settings view',
    type: 'boolean',
  },
  mode_change_duration: {
    description: 'delay between mode change request and retrieval of changed parameters in milliseconds',
    type: 'integer',
    default: 2000,
  },
  grid_button: {
    description: 'whether to show grid on/off button',
    type: 'boolean',
    default: true,
  },
  grid: {
    description: 'whether to show grid',
    type: 'boolean',
  },
  grid_color: {
    description: 'color of the grid',
    type: 'string',
    default: '#00ffff80',
  },
  grid_lat_min: {
    description: 'first lateral distance for grid',
    type: 'number',
    default: 0,
  },
  grid_lat_max: {
    description: 'last lateral distance for grid',
    type: 'number',
    default: 10,
  },
  grid_lon_min: {
    description: 'first longitudal distance for grid',
    type: 'number',
    default: 1,
  },
  grid_lon_max: {
    description: 'last longitudal distance for grid',
    type: 'number',
    default: 25,
  },
  horizon_button: {
    description: 'whether to show horizon on/off button',
    type: 'boolean',
    default: true,
  },
  horizon: {
    description: 'whether to show horizon',
    type: 'boolean',
    default: true,
  },
  cross_button: {
    description: 'whether to show cross on/off button',
    type: 'boolean',
    default: true,
  },
  cross: {
    description: 'whether to show cross',
    type: 'boolean',
  },
  guides_button: {
    description: 'whether to show guides on/off button',
    type: 'boolean',
    default: true,
  },
  guides: {
    description: 'whether to show guides',
    type: 'boolean',
    default: true,
  },
  guide1: {
    description: 'first guide line',
    type: 'number',
    default: 1,
  },
  guide_laterals: {
    description: 'lane guides',
    type: 'array',
    items: { type: 'number' },
    default: [1.6, 5.1, 8.6, 12.1],
  },
  guide_color: {
    description: 'color of the guide lines',
    type: 'string',
    default: 'hsla(51,100%,50%,0.5)',
  },
  guide_thick: {
    description: 'thickness of the guide lines',
    type: 'number',
    default: 2,
  },
  guide_thick1: {
    description: 'thickness of the guide lines',
    type: 'number',
    default: 9,
  },
  guide_text_size: {
    description: 'size of the guide text',
    type: 'number',
    default: 40,
  },
  guide_text_color: {
    description: 'color of the guide text',
    type: 'string',
    default: 'hsla(51, 100%, 41%, 1.00)',
  },
  guide_text_offset: {
    description: 'offset of the guide text from beginning of line in %',
    type: 'number',
    default: 30,
    minimum: 0,
    maximum: 100,
  },
}, meleActor._properties)

export default class meleActorZoom extends meleActor {
  init() {
    const elButtons = this.appendChildren({ tag: 'div', class: 'buttons' })
    this.elFocus = elButtons.appendChildren({ interclick: 0, interlong: 0, tag: 'mele-button', key: 'refocus', class: 'ctrl', iconleft: 'video.refocus' })
    this.elFocus.eventButtonClicked = this.refocus.bind(this)
    this.elPlus = elButtons.appendChildren({ interclick: 300, interlong: 0, tag: 'mele-button', key: 'zoomin', class: 'ctrl', iconleft: 'video.zoomin' })
    this.elPlus.eventButtonClicked = this.zoomIn.bind(this)
    this.elToggle = elButtons.appendChildren({ interclick: 0, interlong: 0, tag: 'mele-button', key: 'zoom', class: 'ctrl', label: 'video.zoom', iconleft: 'settings' })
    this.elToggle.eventButtonClicked = this.toggleSettings.bind(this)
    this.elMinus = elButtons.appendChildren({ interclick: 300, interlong: 0, tag: 'mele-button', key: 'zoomin', class: 'ctrl', iconleft: 'video.zoomout' })
    this.elMinus.eventButtonClicked = this.zoomOut.bind(this)
    super.init()
  } // init

  message({ action, params }) {
    if(params?.channel !== _.natural(this.parentElement.ch)) return

    if(action === 'mmDopplerMeta') {
      this.doppler_meta = params
      this.rig = Algo.rig(params?.calibration, params?.optics)
      let changed = false
      if(!this.canvas) {
        this.createCanvas()
        changed = true
      }
      if(this.last_zoom !== params?.optics?.zoom) {
        this.last_zoom = params?.optics?.zoom
        changed = true
      }
      if(this.h264_meta && !(this.canvas.width > 0 && this.canvas.height > 0)) {
        changed = true
      }
      if(changed) {
        this.redrawCanvas(true)
      }
    }else if(action === 'mmH264Meta') {
      this.h264_meta = params
      this.metaH264(params)
    }
  } // message

  createCanvas() {
    this.canvas = this.appendChildren({ tag: 'canvas' })
    this.canvas.width = 0
    this.canvas.height = 0
    this.canvas.addEventListener('mousedown', this.startGuide.bind(this), { passive: false })
    this.canvas.addEventListener('mousemove', this.moveGuide.bind(this), { passive: false })
    this.canvas.addEventListener('mouseup', this.endGuide.bind(this), { passive: false })
    this.canvas.addEventListener('touchstart', this.startGuide.bind(this), { passive: false })
    this.canvas.addEventListener('touchmove', this.moveGuide.bind(this), { passive: false })
    this.canvas.addEventListener('touchend', this.endGuide.bind(this))
    this.canvas.addEventListener('touchcancel', this.endGuide.bind(this))
    this.ctx = this.canvas.getContext('2d')
  } // createCanvas

  mouseDown() {
    if(this.canvas) return // mouse down is handled by canvas if it exists
    this.refocus()
  } // mouseDown

  metaH264(params) {
    let zoom = _.roundBy(parseFloat(params.zoom), 2)
    if(!(zoom >= 1)) zoom = 0
    this.zoom = zoom

    const live = this.closest('mele-player').live

    this.elFocus.disabled = !live
    if(zoom) {
      // camera is zoomable and zoom is set
      this.elToggle.label2 = zoom.toFixed(1) + 'x'
      this.elMinus.disabled = !live
      this.elPlus.disabled = !live
    }else if(params.zoom_max > 1) {
      // camera is zoomable but zoom is not set
      this.elToggle.label2 = '??? x'
      this.elMinus.disabled = !live || zoom < 1.01
      this.elPlus.disabled = !live || zoom > params.zoom_max - 0.01
    }else {
      // camera is not zoomable
      this.elToggle.label2 = '1.0x'
      this.elMinus.disabled = true
      this.elPlus.disabled = true
    }

    if(this.settings_view) {
      for(const el of this.settings_view?.findAll('mele-input')) {
        if(el.closest('.switches')) continue // skip switches
        el.disabled = !live
      }
    }
  } // message

  async refocus() {
    try{
      const player = this.closest('mele-player')
      const channel = _.natural(this.parentElement.ch)
      await player.listener.request(player.source.replace('msysStreamer', 'msvcCameraAxis'), 'mmRefocus', { channel }, { request: 'mmSuccess', timeout: 10000 })
    }catch(e) {
      this.error('Error refocusing camera: '+e)
    }
  } // refocus

  zoomLevels() {
    let zoom_max = this.h264_meta?.zoom_max
    if(!(zoom_max >= 1)) zoom_max = 0
    let levels = Array.isArray(this.levels) ? this.levels : []

    if(levels.length === 0) {
      // no custom zoom levels, use default ones by +0.5x
      for(let i=1; i<=zoom_max; i+=0.5) {
        levels.push(_.roundBy(i, 1))
      }
    }

    levels = levels.map((z) => parseFloat(z)).filter((z) => z >= 1).sort((a, b)=>a-b)
    if(zoom_max) {
      while(levels[levels.length-1] > zoom_max) {
        levels.pop()
      }
    }

    if(levels[0] !== 1) {
      levels.unshift(1)
    }

    if(zoom_max && levels[levels.length-1] < zoom_max) {
      levels.push(zoom_max)
    }

    return levels
  } // zoomLevels

  zoomIn(el, count) {
    const levels = this.zoomLevels()
    const zoom = this.zooming_zoom || this.zoom || 1
    const zoom_max = this.h264_meta?.zoom_max >= 1 ? this.h264_meta.zoom_max : 0
    let new_zoom = zoom
    if(!(new_zoom >= 1)) new_zoom = 0
    for(let i=0; i<count; i++) {
      new_zoom = levels.find((z) => z > new_zoom)
      if(!new_zoom) {
        if(zoom_max) {
          new_zoom = zoom_max
        }else {
          new_zoom = levels[levels.length-1]
          if(!(new_zoom >= 1)) {
            new_zoom = 1
          }
        }
      }
    }

    if(zoom_max && new_zoom > zoom_max) {
      new_zoom = zoom_max
    }
    this.setZoom(new_zoom)
  } // zoomIn

  zoomOut(el, count) {
    const levels = this.zoomLevels()
    const zoom = this.zooming_zoom || this.zoom || 1
    let new_zoom = zoom
    for(let i=0; i<count; i++) {
      new_zoom = levels.findLast((z) => z < new_zoom)
      if(!new_zoom) break
    }
    if(!(new_zoom >= 1)) {
      new_zoom = 1
    }
    this.setZoom(new_zoom)
  } // zoomOut

  async setZoom(zoom) {
    if(isNaN(zoom) || !(zoom>0)) return

    if(this.zooming_ender) {
      clearTimeout(this.zooming_ender)
    }
    this.zooming_ender = setTimeout(() =>{
      delete this.zooming_ender
      if(Math.abs(this.zooming_zoom - this.zoom) > 0.01) {
        this.error('Zooming ended with different zoom level than requested: '+this.zoom+' != '+this.zooming_zoom)
      }
      this.zooming = false
      this.zooming_dt = null
      this.zooming_zoom = null
    }, 2000)

    const channel = _.natural(this.parentElement.ch)
    try{
      this.zooming_dt = Date.now()
      this.zooming_zoom = zoom
      const player = this.closest('mele-player')
      await player.listener.request(player.source.replace('msysStreamer', 'msvcCameraAxis'), 'mmSetZoom', { channel, zoom }, { request: 'mmZoom', timeout: 10000 })
    }catch(e) {
      this.error('Zoom error: '+String(e))
    }
  } // setZoom

  async toggleSettings() {
    if (this._settingsToggling) return
    this._settingsToggling = true
    const on = this.settings = !this.settings
    this.elToggle.wait= ' '
    if(!on) {
      if(this.settings_view) {
        this.settings_view.remove()
        document.removeEventListener('mousedown', this._onSettingsOutsideClick)
        document.removeEventListener('touchstart', this._onSettingsOutsideClick)
        delete this.settings_view
        this._settingsToggling = false
        this.elToggle.wait = ''
      }
      return
    }

    // obtain values from camera
    const values = await this.getSettingsFromCamera()
    this._settingsToggling = false
    this.elToggle.wait = ''

    const live = this.closest('mele-player').live
    const disabled = !live
    this.settings_view = this.appendChildren({
      tag: 'mele-view',
      key: 'settings',
      items: [
        /* eslint-disable no-template-curly-in-string */
        { key: 'mode', tag: 'mele-input', type: 'grid', prefix: 'video.mode.', itemicon: 'icon:${prefix}${key}', auto: true, blank: true, disabled },
        { key: 'contrast', tag: 'mele-input', type: 'rotarystring', label: 'video.config.${key}', round: 5, min: 0, max: 100, radian: 100, minor: 5, major: 10, disabled },
        { key: 'brightness', tag: 'mele-input', type: 'rotarystring', label: 'video.config.${key}', round: 5, min: 0, max: 100, radian: 100, minor: 5, major: 10, disabled },
        { key: 'sharpness', tag: 'mele-input', type: 'rotarystring', label: 'video.config.${key}', round: 5, min: 0, max: 100, radian: 100, minor: 5, major: 10, disabled },
        { key: 'exposure', tag: 'mele-input', type: 'rotarystring', label: 'video.config.${key}', round: 5, min: 0, max: 100, radian: 100, minor: 5, major: 10, disabled },
        { key: 'irfilter', tag: 'mele-input', type: 'grid', label: 'video.config.${key}', prefix: 'video.config.irfilter.', itemlabel: '${prefix}${key}', auto: true, disabled },
        { key: 'switches', tag: 'div' },
        /* eslint-enable no-template-curly-in-string */
      ],
    })
    this.settings_view.eventValueChanged = this.valueChanged.bind(this)

    // Add outside click listener
    this._onSettingsOutsideClick = (e) => {
      if (!this.settings_view.contains(e.target) && !this.elToggle.contains(e.target)) {
        this.toggleSettings() // will remove settings_view and listener
      }
    }
    document.addEventListener('mousedown', this._onSettingsOutsideClick)
    document.addEventListener('touchstart', this._onSettingsOutsideClick)

    if(this.doppler_meta) {
      // create switch buttons as configured
      const switches = this.settings_view.find('@switches')
      for(const key of ['cross', 'horizon', 'grid', 'guides']) {
        if(!this[key+'_button']) continue // skip if button should not be shown
        const btn = switches.appendChildren({ key, tag: 'mele-button', interclick: 0, interlong: 0, type: 'toggle', label: `doppler.${key}` })
        btn.selected = !!this[key]
        btn.eventButtonClicked = () => {
          btn.selected = !btn.selected
          this[key] = btn.selected
          this.redrawCanvas()
        }
      }
    }

    this.settings_view.setValues(values)
  } // toggleSettings

  async getSettingsFromCamera() {
    const values = {
      contrast: 50,
      brightness: 50,
      sharpness: 50,
      exposure: 50,
      irfilter: 'auto',
    }
    try{
      const channel = parseInt(this.parentElement.ch)
      const { params: { params } } = await this.closest('.mele-player').listener.request('SERVER@msvcCameraAxis', 'mmGetParams', { channel })
      if(params) {
        const sensor = (_.natural(this.h264_meta?.sensor) || 1) - 1
        values.contrast = parseInt(params[`root.ImageSource.I${sensor}.Sensor.Contrast`]) || 50
        values.brightness = parseInt(params[`root.ImageSource.I${sensor}.Sensor.Brightness`]) || 50
        values.sharpness = parseInt(params[`root.ImageSource.I${sensor}.Sensor.Sharpness`]) || 50
        values.exposure = parseInt(params[`root.ImageSource.I${sensor}.Sensor.ExposureValue`]) || 50
        values.irfilter = params[`root.ImageSource.I${sensor}.DayNight.IrCutFilter`] || 'auto'
      }
    }catch(e) {
      this.error(e)
    }

    return values
  } // getSettingsFromCamera

  valueChanged(el, val) {
    const channel = parseInt(this.parentElement.ch)
    if(el.key==='mode') {
      val = String(val??'')
      if(val==='') return
      this.closest('.mele-player').listener.publish('SERVER@msvcCameraAxis', 'mmSetParams', { channel, params: { preset: val } })
      setTimeout(async ()=>{
        const values = await this.getSettingsFromCamera()
        if(this.settings_view) {
          this.settings_view.setValues(values)
        }
      }, this.mode_change_duration)
    }else {
      let params
      const sensor = (_.natural(this.h264_meta?.sensor) || 1) - 1
      if(el.key==='contrast') {
        params = { [`ImageSource.I${sensor}.Sensor.Contrast`]: el.value }
      }else if(el.key==='brightness') {
        params = { [`ImageSource.I${sensor}.Sensor.Brightness`]: el.value }
      }else if(el.key==='sharpness') {
        params = { [`ImageSource.I${sensor}.Sensor.Sharpness`]: el.value }
      }else if(el.key==='exposure') {
        params = { [`ImageSource.I${sensor}.Sensor.ExposureValue`]: el.value }
      }else if(el.key==='irfilter') {
        params = { [`ImageSource.I${sensor}.DayNight.IrCutFilter`]: el.value }
      }
      if(params) {
        this.closest('.mele-player').listener.publish('SERVER@msvcCameraAxis', 'mmSetParams', { channel, params })
      }
    }
  } // valueChanged

  redrawCanvas(enforce=false) {
    const { doppler_meta, h264_meta, grid, horizon, cross, guides, canvas, ctx } = this
    const { h_pixels, v_pixels, h_angle, v_angle } = h264_meta || {}
    const { h_angle: doppler_h_angle, v_angle: doppler_v_angle } = doppler_meta?.optics || {}
    const { camera_height, camera_side, camera_forward, camera_yaw, camera_pitch, camera_roll } = doppler_meta?.calibration || {}
    const params = { doppler_h_angle, doppler_v_angle, h_pixels, v_pixels, h_angle, v_angle, camera_height, camera_side, camera_forward, camera_yaw, camera_pitch, camera_roll, grid, horizon, cross, guides }
    if(!enforce && canvas.drawn && !Object.keys(params).some(key => canvas.drawn[key] !== params[key])) return
    canvas.drawn = params

    if(!grid && !horizon && !cross && !guides) {
      canvas.style.display = 'none'
      return
    }

    if(!(h_pixels > 0 && v_pixels > 0)) {
      return
    }

    Draw.clear(ctx)
    canvas.style.display = 'block'
    canvas.width = h_pixels
    canvas.height = v_pixels

    // draw central cross if required
    if(cross) {
      const style = ['#ff44ff80', 3]
      Draw.drawLine(ctx, 0, v_pixels/2, h_pixels, v_pixels/2, ...style)
      Draw.drawLine(ctx, h_pixels/2, 0, h_pixels/2, v_pixels, ...style)
    }

    const rig = this.rig
    if(!rig) return
    const { project } = rig

    // bounds used to clip lines
    const bounds = [0, h_pixels, 0, v_pixels]

    function drawLine(p1, p2, color, thick) {
      const clipped = Algo.clipLineInRect(p1, p2, ...bounds)
      if(clipped.length !== 2) return []
      const [point1, point2] = clipped
      Draw.drawLine(ctx, ...point1, ...point2, color, thick)
      return [point1, point2]
    }

    // draw grid if required
    if(grid) {
      let { grid_color, grid_lat_min, grid_lat_max, grid_lon_min, grid_lon_max } = this

      // this is to let it work for both positive and negative the same way
      grid_lat_min = Math.min(Math.abs(grid_lat_min), Math.abs(grid_lat_max))
      grid_lat_max = Math.max(Math.abs(grid_lat_min), Math.abs(grid_lat_max))

      // draw lateral lines
      for(let car_lat=grid_lat_min; car_lat<=grid_lat_max; car_lat++) {
        const lat = -(car_lat + camera_side)
        const p1 = project([1, lat, -camera_height], true)
        const p2 = project([1_000_000, lat, -camera_height], true)
        drawLine(p1, p2, grid_color, 1)
      }

      // draw longitudinal lines
      for(let car_lon=grid_lon_min; car_lon<=grid_lon_max; car_lon++) {
        const lon = car_lon + camera_forward
        const p1 = project([lon, -grid_lat_min + 1, -camera_height], true)
        const p2 = project([lon, -grid_lat_max - 1, -camera_height], true)
        drawLine(p1, p2, grid_color, car_lon % 5 === 0 ? 2 : 1)
      }
    }

    // draw horizon if required
    if(horizon) {
      const left = project([1000000, -1000000, -camera_height], true)
      const right = project([1000000, -1, -camera_height], true)
      drawLine(left, right, 'hsla(200, 97.30%, 44.30%, 0.8)', 5)
    }

    // draw guides if required
    if(guides) {
      for(const car_lat of this.guide_laterals) {
        // calculate guide line
        const lat = -(car_lat + camera_side)
        const near = project([1, lat, -camera_height], true)
        const far = project([1_000_000, lat, -camera_height], true)
        const thick = car_lat===this.guide_laterals[0] ? this.guide_thick1 : this.guide_thick
        const [p1, p2] = drawLine(near, far, this.guide_color, thick)
        if(!p1) continue // line is not visible

        drawMoveIndicators(p1, p2, this.guide_color)

        // point for drawing guide distance
        const { guide_text_offset, guide_text_size } = this
        const from_beginning = guide_text_offset / 100
        const x1 = p1[0] + from_beginning * (p2[0] - p1[0])
        const y1 = p1[1] + from_beginning * (p2[1] - p1[1])
        const text = car_lat.toFixed(1) + 'm'
        Draw.drawText(ctx, text, guide_text_size, x1, y1 + guide_text_size / 2, 'left', this.guide_text_color)
      }
    }

    function drawMoveIndicators(p1, p2, fill) {
      const t = 0.1 // 10% from line beginning
      const gap = 4
      const width = 24
      const height = 16
      const x1 = p1[0]
      const y1 = p1[1]
      const x2 = p2[0]
      const y2 = p2[1]

      const dx = x2 - x1
      const dy = y2 - y1
      const len = Math.hypot(dx, dy) || 1
      const ux = dx / len
      const uy = dy / len

      const nx = -uy
      const ny = ux

      const px = x1 + t * dx
      const py = y1 + t * dy

      const drawTri = sign => {
        const baseCx = px + sign * gap * nx
        const baseCy = py + sign * gap * ny
        const tipX = px + sign * (gap + height) * nx
        const tipY = py + sign * (gap + height) * ny
        const halfW = width / 2

        const b1x = baseCx + (-halfW) * ux
        const b1y = baseCy + (-halfW) * uy
        const b2x = baseCx + (halfW) * ux
        const b2y = baseCy + (halfW) * uy

        ctx.beginPath()
        ctx.moveTo(tipX, tipY)
        ctx.lineTo(b1x, b1y)
        ctx.lineTo(b2x, b2y)
        ctx.closePath()
        ctx.fillStyle = fill
        ctx.fill()
      }

      drawTri(+1)
      drawTri(-1)
    }
  } // redrawCanvas

  latLonFromEvent(event) {
    const { x, y } = Draw.eventXY(event)
    if(isNaN(x) || isNaN(y)) return []
    if(!this.rig) return []
    const { camera_side, camera_forward, camera_height, rotationMatrix, cameraMatrix } = this.rig
    const [cam_lon, cam_lat] = Algo.unprojectZ([x, y], -camera_height, rotationMatrix, cameraMatrix)
    const car_lon = cam_lon - camera_forward
    const car_lat = -cam_lat - camera_side
    return [car_lon, car_lat]
  } // latLonFromEvent

  startGuide(event) {
    event.preventDefault()
    const [, cursor_lat] = this.latLonFromEvent(event)
    if(isNaN(cursor_lat)) return
    const guides = this.guide_laterals || []
    let closest_guide_idx = -1
    for(let i=0; i<guides.length; i++) {
      const distance = Math.abs(guides[i] - cursor_lat)
      if(distance < 0.5 && (closest_guide_idx<0 || distance < Math.abs(guides[closest_guide_idx] - cursor_lat))) {
        closest_guide_idx = i
      }
    }
    if(closest_guide_idx >= 0) {
      this.moving_guide = closest_guide_idx
      this.redrawCanvas(true)
    }else {
      if(!isNaN(this.moving_guide)) {
        delete this.moving_guide
        this.redrawCanvas(true)
      }
      this.refocus()
    }
  } // startGuide

  moveGuide(event) {
    event.preventDefault()
    const [, cursor_lat] = this.latLonFromEvent(event)
    if(isNaN(cursor_lat)) return

    const idx = this.moving_guide
    if(!(idx >= 0)) return // no guide is being moved

    if(idx === 0 && cursor_lat < 0) return
    if(idx > 0 && cursor_lat < this.guide_laterals[idx-1] - 1) return

    const change = cursor_lat - this.guide_laterals[idx]
    for(let i=idx; i<this.guide_laterals.length; i++) {
      this.guide_laterals[i] += change
    }

    this.redrawCanvas(true)
  } // moveGuide

  endGuide() {
    if(!isNaN(this.moving_guide)) {
      delete this.moving_guide
      this.redrawCanvas(true)
    }
  } // endGuide
} // class meleActorZoom

meleActorZoom._properties = PROPERTIES
meleActorZoom.observedAttributes = _.keys(PROPERTIES)
window.customElements.define('mele-actor-zoom', meleActorZoom)
